import React from 'react';
import { formatCurrency } from '../../utils/formatters';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
    period: string;
  };
  icon: React.ReactNode;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo';
  format?: 'currency' | 'number' | 'percentage';
}

const accentMap: Record<string, { border: string; icon: string; glow: string; value: string }> = {
  blue:   { border: 'rgba(59,130,246,0.2)',  icon: 'rgba(59,130,246,0.1)',  glow: 'rgba(59,130,246,0.08)',  value: '#2563eb' },
  green:  { border: 'rgba(16,185,129,0.2)',  icon: 'rgba(16,185,129,0.1)',  glow: 'rgba(16,185,129,0.08)',  value: '#059669' },
  red:    { border: 'rgba(239,68,68,0.18)',  icon: 'rgba(239,68,68,0.09)',  glow: 'rgba(239,68,68,0.06)',   value: '#dc2626' },
  yellow: { border: 'rgba(217,119,6,0.22)',  icon: 'rgba(217,119,6,0.1)',   glow: 'rgba(217,119,6,0.08)',   value: '#d97706' },
  purple: { border: 'rgba(139,92,246,0.2)',  icon: 'rgba(139,92,246,0.1)',  glow: 'rgba(139,92,246,0.08)',  value: '#7c3aed' },
  indigo: { border: 'rgba(99,102,241,0.2)',  icon: 'rgba(99,102,241,0.1)',  glow: 'rgba(99,102,241,0.08)',  value: '#4f46e5' },
};

const iconColorMap: Record<string, string> = {
  blue:   '#3b82f6',
  green:  '#10b981',
  red:    '#ef4444',
  yellow: '#d97706',
  purple: '#8b5cf6',
  indigo: '#6366f1',
};

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  icon,
  color = 'blue',
  format = 'number'
}) => {
  const formatValue = (val: string | number) => {
    const numValue = typeof val === 'string' ? parseFloat(val) || 0 : (val ?? 0);
    switch (format) {
      case 'currency':   return formatCurrency(numValue);
      case 'percentage': return `${numValue}%`;
      default:           return numValue.toLocaleString();
    }
  };

  const accent = accentMap[color] ?? accentMap.blue;
  const iconColor = iconColorMap[color] ?? iconColorMap.blue;

  return (
    <div
      style={{
        background: '#ffffff',
        border: `1px solid ${accent.border}`,
        borderRadius: 12,
        padding: '20px 22px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, transform 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 24px rgba(0,0,0,0.08)`;
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
      }}
    >
      {/* Colored top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: iconColor,
        borderRadius: '12px 12px 0 0',
      }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
        {/* Text content */}
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#8a8778',
            marginBottom: 8,
          }}>
            {title}
          </div>
          <div style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            color: accent.value,
            letterSpacing: '-0.02em',
          }}>
            {formatValue(value)}
          </div>

          {change && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                fontSize: 11,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                color: change.type === 'increase' ? '#059669'
                      : change.type === 'decrease' ? '#dc2626'
                      : '#6b6a5c',
                display: 'flex', alignItems: 'center', gap: 2,
              }}>
                {change.type === 'increase' ? '↑' : change.type === 'decrease' ? '↓' : '—'}
                {Math.abs(change.value)}%
              </span>
              <span style={{ fontSize: 11, fontFamily: "'Inter', sans-serif", color: '#8a8778' }}>
                vs {change.period}
              </span>
            </div>
          )}
        </div>

        {/* Icon container */}
        <div style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          background: accent.icon,
          border: `1px solid ${accent.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}>
          <div style={{ width: 20, height: 20 }}>
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
};
